import { useState, useEffect } from "react";
import Breadcrumb from "../../components/Breadcrumb";
import Card from "../../components/Card";
import TableHeader from "../../components/offices/TableHeader";
import Modal from "../../components/modal/Modal";
import OfficeForm from "../../components/offices/OfficeForm";
import { useQuery } from "@apollo/client";
import {
  GET_COMPANY_OFFICES,
  GET_COMPANY_USERS,
} from "../../graphql/companyQueries";
import { GET_ALL_COMPANY_OFFICES } from "../../graphql/officeQueries";
import { formatPhoneNumber } from "../../utils/phoneFormat";
import { useParams } from "react-router-dom";
import PrimaryOfficeAction from "../../components/offices/PrimaryOfficeAction";
import Toast from "../../components/Toast/Toast";
import {
  EmptyState,
  ErrorBanner,
  ErrorState,
  skeletonTableProps,
} from "../../components/states";
import { Table } from "antd";
import useDebounce from "../../hooks/useDebounce";
import TableToolbar from "../../components/TableToolbar/TableToolbar";
import { toServerSort } from "../../utils/tableSort";
import { TABLE_SCROLL } from "../../constants/table";

// Columns the server can order the offices by.
const SORTABLE_COLUMNS = ["name", "phone", "isDefault"];

const Offices = () => {
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalCount: 0,
  });
  const [showModal, setShowModal] = useState(false);
  const [agentsModal, setAgentsModal] = useState(false);
  const [formType, setFormType] = useState("add");
  const [offices, setOffices] = useState([]);
  const [agents, setAgents] = useState([]);
  const [office, setOffice] = useState({});
  const [searchText, setSearchText] = useState("");
  const [sort, setSort] = useState(null);

  const debounceValue = useDebounce(searchText, 500);

  const { companyId } = useParams();

  const { loading, error, data, refetch } = useQuery(GET_ALL_COMPANY_OFFICES, {
    variables: {
      companyId,
      pagination: {
        page: pagination.page,
        limit: pagination.limit,
      },
      searchText: debounceValue,
      ...(sort && { sort }),
    },
    fetchPolicy: "no-cache",
  });

  const { data: users } = useQuery(GET_COMPANY_USERS, {
    variables: { companyId },
    fetchPolicy: "no-cache",
    onError(err) {
      Toast(
        "error",
        "There was an error loading the users. If the issue persists please contact the administrators"
      );
    },
  });

  const onChange = (nextPagination, _tableFilters, sorter, extra) => {
    // A new sort order starts again from the first page.
    if (extra?.action === "sort") {
      setSort(toServerSort(sorter, SORTABLE_COLUMNS));
      setPagination((prev) => ({ ...prev, page: 1 }));
      return;
    }
    setPagination((prev) => ({
      ...prev,
      page: nextPagination?.current,
      limit: nextPagination?.pageSize,
    }));
  };

  const columns = [
    {
      title: "Office Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      render: (text) => <span className="font-medium text-black">{text}</span>,
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      render: (_, record) => (
        <div className="font-medium text-black">
          {record.address && <div>{`${record.address},`}</div>}
          <div>{`${record.city}, ${record.state}, ${record.zip}`}</div>
        </div>
      ),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      className: "whitespace-nowrap",
      sorter: true,
      align: "center",
      render: (phone) => (
        <span className="font-medium text-black">
          {formatPhoneNumber(phone)}
        </span>
      ),
    },
    {
      title: "Primary Office",
      dataIndex: "isDefault",
      key: "isDefault",
      sorter: true,
      align: "center",
      render: (isDefault) => (
        <div className="flex justify-center">
          <PrimaryOfficeAction isDefault={isDefault} />
        </div>
      ),
    },
    {
      title: "Agents",
      key: "agents",
      align: "center",
      render: (_, record) => (
        <div className="flex font-medium text-black justify-center">
          <button
            onClick={() => {
              setAgents(
                users?.getCompanyUsers.filter(
                  (user) => user.defaultOffice?.name === record.name
                )
              );
              setAgentsModal(true);
            }}
            className="flex space-x-2 items-center bg-primary text-white rounded-full px-4 py-1 hover:opacity-90"
          >
            View
          </button>
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      skeleton: "actions",
      align: "center",
      render: (_, record) => (
        <div className="flex justify-center align-center space-x-3.5">
          <button
            className="hover:text-primary"
            onClick={() => handleEdit(record)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="w-5 h-5"
            >
              <path d="M5.433 13.917l1.262-3.155A4 4 0 017.58 9.42l6.92-6.918a2.121 2.121 0 013 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 01-.65-.65z" />
              <path d="M3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0010 3H4.75A2.75 2.75 0 002 5.75v9.5A2.75 2.75 0 004.75 18h9.5A2.75 2.75 0 0017 15.25V10a.75.75 0 00-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5z" />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (data?.getAllCompanyOffice?.offices)
      setOffices(data.getAllCompanyOffice?.offices);
    setPagination((prev) => ({
      ...prev,
      totalCount: data?.getAllCompanyOffice?.totalCount,
    }));
  }, [data]);

  const handleEdit = (editOffice) => {
    setOffice(editOffice);
    setFormType("edit");
    setShowModal(true);
  };
  const handleModalRender = () => {
    setFormType("add");
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setOffice({});
  };

  const firstLoad = loading && !offices.length;

  return (
    <div id="companies">
      <Breadcrumb pageName="Offices" />
      <div className="py-6">
        <Card>
          <TableToolbar
            title="All Offices"
            count={pagination.totalCount || 0}
            searchValue={searchText}
            onSearch={setSearchText}
            action={
              <button
                onClick={handleModalRender}
                className="flex space-x-2 items-center bg-primary text-white rounded-sm px-4 py-1 hover:opacity-90"
              >
                <svg
                  className="fill-current"
                  width="20"
                  height="20"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
                </svg>
                <span>Add an office</span>
              </button>
            }
          />
          {error && !offices.length ? (
            <ErrorState onRetry={() => refetch()} />
          ) : (
            <>
              {error && <ErrorBanner onRetry={() => refetch()} />}
              <Table
                scroll={TABLE_SCROLL}
                bordered
                rowKey="_id"
                onChange={onChange}
                {...(firstLoad
                  ? skeletonTableProps(columns)
                  : {
                      columns,
                      dataSource: offices,
                      pagination: {
                        position: ["bottomCenter"],
                        current: pagination.page,
                        pageSize: pagination.limit,
                        total: pagination.totalCount,
                      },
                    })}
                locale={{
                  emptyText: searchText ? (
                    <EmptyState
                      title="No offices match your search"
                      description={`Nothing found for "${searchText}".`}
                      actionLabel="Clear search"
                      onAction={() => setSearchText("")}
                    />
                  ) : (
                    <EmptyState
                      title="No offices yet"
                      description="Offices you add for this company will appear here."
                    />
                  ),
                }}
              />
            </>
          )}
        </Card>

        <Modal
          title={formType === "add" ? "Add Office" : "Edit Office"}
          showModal={showModal}
          setShowModal={(value) => {
            setShowModal(value);
            if (!value) setOffice({});
          }}
        >
          <OfficeForm
            office={office}
            companyId={companyId}
            formType={formType}
            handleClose={handleClose}
            allOffices={offices.map((o) => ({ id: o._id, name: o.name }))}
            refetchOffices={refetch}
          />
        </Modal>
        <Modal
          title="Assigned Agents"
          showModal={agentsModal}
          setShowModal={(value) => {
            setAgentsModal(value);
            setAgents([]);
          }}
        >
          {agents?.length ? (
            <ul>
              {agents?.map((agent, index) => (
                <li key={agent.email}>
                  {index + 1}. {agent.firstName} {agent.lastName} -{" "}
                  {agent.email}
                </li>
              ))}
            </ul>
          ) : (
            <p>No agents assigned.</p>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default Offices;
